import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiUrl } from '../config/api.config';

// NOTE: Do NOT call getApiUrl() at module load time!
// It must be called at request time when window.location is available.

// Helper to get axios config with runtime API URL
const getAxiosConfig = () => ({
  withCredentials: true,
  timeout: 10000
});

// Async thunks - use getApiUrl() at request time, not module load time
export const fetchUserLists = createAsyncThunk(
  'lists/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${getApiUrl()}/lists`, getAxiosConfig());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Request failed');
    }
  }
);

export const saveList = createAsyncThunk(
  'lists/save',
  async ({ name, tickers }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${getApiUrl()}/lists/${encodeURIComponent(name)}`,
        { tickers },
        getAxiosConfig()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Request failed');
    }
  }
);

export const deleteList = createAsyncThunk(
  'lists/delete',
  async (name, { rejectWithValue }) => {
    try {
      await axios.delete(`${getApiUrl()}/lists/${encodeURIComponent(name)}`, getAxiosConfig());
      return name;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Request failed');
    }
  }
);

// Lists slice
const listsSlice = createSlice({
  name: 'lists',
  initialState: {
    lists: {},
    // Names of lists that started as GICS sector defaults (for grouping only;
    // they are ordinary lists in every other respect).
    defaultListNames: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch lists
      .addCase(fetchUserLists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserLists.fulfilled, (state, action) => {
        state.loading = false;
        state.lists = action.payload.lists;
        state.defaultListNames = action.payload.defaultLists || [];
      })
      .addCase(fetchUserLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Save list
      .addCase(saveList.fulfilled, (state, action) => {
        state.lists[action.payload.name] = action.payload.tickers;
      })
      // Delete list
      .addCase(deleteList.fulfilled, (state, action) => {
        delete state.lists[action.payload];
        state.defaultListNames = state.defaultListNames.filter(n => n !== action.payload);
      });
  },
});

export const { clearError } = listsSlice.actions;
export default listsSlice.reducer;